// Location Context on the browser side: the destination the SERVER currently
// uses for this visitor (server_elakay elakay_services/locationContext.js),
// asked once and shared by every view that needs to label or recompute
// something with it (location filter bar, Product Preview shipping/taxes).
//
//   source: "target" | "selected_address" | "default_address" | "ip" | null
//   isApproximate: true for an IP location -- never shown as a final address
//
// - one GET /location/context per (Target Location, signed-in user), cached
//   in memory + sessionStorage for the session, deduplicated while in flight
//   (no IP lookup per card / per page);
// - refetched when the Target Location changes (BUYER_LOCATION_EVENT) or an
//   address / sign-in changes (LOCATION_INPUTS_EVENT);
// - a failure is "location unavailable" (status "error"), never a crash: the
//   server keeps applying its own fallback on every request anyway.
// The browser never sends IP-derived coordinates back: the server resolves
// the IP itself on each request.
import { useEffect, useState } from "react";
import { elakay_API } from "../config";
import { isAuthenticated } from "../includes/auth";
import { BUYER_LOCATION_EVENT, LOCATION_INPUTS_EVENT, locationQueryParams } from "./buyerLocation";

const STORAGE_KEY = "elakay_location_context";
const TTL_MS = 30 * 60 * 1000;
const EMPTY_TTL_MS = 60 * 1000;

let memory = null; // { key, location, expiresAt }
const inFlight = new Map(); // key -> Promise<location|null>
const listeners = new Set();

const authToken = () => {
  const token = isAuthenticated()?.token;
  return typeof token === "string" && token.split(".").length === 3 ? token : "";
};

const contextKey = () => JSON.stringify({ target: locationQueryParams(undefined), user: authToken().slice(-16) });

const readStored = (key) => {
  if (memory && memory.key === key && memory.expiresAt > Date.now()) return memory;
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || "null");
    if (stored && stored.key === key && stored.expiresAt > Date.now()) {
      memory = stored;
      return stored;
    }
  } catch (_) { /* storage unavailable: memory only */ }
  return null;
};

const store = (entry) => {
  memory = entry;
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry)); } catch (_) { /* ignore */ }
};

export const clearLocationContextCache = () => {
  memory = null;
  inFlight.clear();
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
};

// -> Promise<location|null>. Never rejects.
export const fetchLocationContext = ({ force = false } = {}) => {
  const key = contextKey();
  if (!force) {
    const hit = readStored(key);
    if (hit) return Promise.resolve(hit.location);
    if (inFlight.has(key)) return inFlight.get(key);
  }
  const query = new URLSearchParams(
    Object.entries(locationQueryParams(undefined)).map(([k, v]) => [k, String(v)])
  ).toString();
  const token = authToken();
  const promise = fetch(`${elakay_API}/location/context${query ? `?${query}` : ""}`, {
    method: "GET",
    headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
    .then((data) => {
      const location = data?.location?.available ? data.location : null;
      // "No destination yet" is only kept briefly (the server may resolve
      // one a moment later, e.g. after a restart or a slow IP lookup).
      store({ key, location, expiresAt: Date.now() + (location ? TTL_MS : EMPTY_TTL_MS) });
      return location;
    })
    // Network / server failure: not cached, the next view asks again.
    .catch(() => null)
    .finally(() => inFlight.delete(key));
  inFlight.set(key, promise);
  return promise;
};

const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener(BUYER_LOCATION_EVENT, notify);
  window.addEventListener(LOCATION_INPUTS_EVENT, () => {
    clearLocationContextCache();
    notify();
  });
}

// React hook: { status: "loading" | "ready" | "unavailable", location }.
// `location` is null when the server has no destination at all (no target,
// no address, IP unknown) -- callers then keep their own fallback behavior.
export const useLocationContext = () => {
  const [state, setState] = useState(() => {
    const hit = typeof window !== "undefined" ? readStored(contextKey()) : null;
    return hit ? { status: "ready", location: hit.location } : { status: "loading", location: null };
  });
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const onChange = () => setVersion((v) => v + 1);
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, []);

  useEffect(() => {
    let active = true;
    const hit = readStored(contextKey());
    if (hit) {
      setState((prev) => (prev.status === "ready" && prev.location === hit.location ? prev : { status: "ready", location: hit.location }));
      return () => { active = false; };
    }
    setState((prev) => ({ status: "loading", location: prev.location }));
    fetchLocationContext().then((location) => {
      if (!active) return;
      const stored = readStored(contextKey());
      setState(stored ? { status: "ready", location } : { status: "unavailable", location: null });
    });
    return () => { active = false; };
  }, [version]);

  return state;
};

// Short human label: "Brooklyn, NY, 11201, US".
export const describeLocationContext = (location) => (location?.label || "");
